import { z } from "zod";
import { notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

export type Post = {
  slug: string;
  title: string;
  date: Date;
  image: string;
  contentHtml: string;
  summary: string;
  keywords: string[];
  imageFocalPoint?: { x: number; y: number };
};

// Define the input schema using Zod
const getPostInputSchema = z.object({
  slug: z.string(),
});

// Server function to fetch and process a single post
export const getPost = createServerFn({ method: "GET" })
  .validator(getPostInputSchema)
  .handler(async (ctx) => {
    // Dynamic imports for server-side code
    const fs = await import("node:fs/promises");
    const path = await import("node:path");
    const matter = (await import("gray-matter")).default;
    const { marked } = await import("marked");

    const postsDirectory = path.join(process.cwd(), "content/blog");
    const fullPath = path.join(postsDirectory, `${ctx.data.slug}.md`);
    let fileContents: string | undefined = undefined;

    try {
      fileContents = await fs.readFile(fullPath, "utf8");
    } catch (err: unknown) {
      console.error("Error reading post file:", ctx.data.slug, err);
      throw notFound(); // Throw TanStack Router's notFound
    }

    // Use gray-matter to parse the post metadata section
    const matterResult = matter(fileContents);
    // Use marked to convert markdown into HTML
    const contentHtml = await marked(matterResult.content);

    // Combine the data with the id and contentHtml
    return {
      ...(matterResult.data as Post),
      contentHtml,
    };
  });
